/**
 * Cache for "Try an Activity" (Step 3) in Learn Art History flow.
 * Stores generated activities in Firestore by artwork pair so the same
 * combination is not regenerated.
 */

import { db } from '../firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';

const COLLECTION = 'flowActivityCache';

/**
 * Stable cache key for an artwork pair (order-independent).
 * Same pair in either order produces the same key.
 * @param {Object} artwork1 - { id?, imageUrl? }
 * @param {Object} artwork2 - { id?, imageUrl? }
 * @returns {string} Document ID for Firestore
 */
export function getFlowActivityCacheKey(artwork1, artwork2) {
  const id1 = artwork1?.id || artwork1?.imageUrl || '';
  const id2 = artwork2?.id || artwork2?.imageUrl || '';
  const sorted = [id1, id2].filter(Boolean).sort();
  if (sorted.length === 0) return '';
  const key = sorted.join('::');
  let h = 0;
  for (let i = 0; i < key.length; i++) {
    h = (h * 31 + key.charCodeAt(i)) >>> 0;
  }
  return `flow_${h.toString(36)}`;
}

/**
 * Get cached flow activity for an artwork pair, if any.
 * @param {Object} artwork1 - Primary artwork
 * @param {Object} artwork2 - Comparison artwork
 * @returns {Promise<{ title: string, objective?: string, materials: string[], steps: string[] } | null>}
 */
export async function getCachedFlowActivity(artwork1, artwork2) {
  const cacheKey = getFlowActivityCacheKey(artwork1, artwork2);
  if (!cacheKey) return null;
  try {
    const ref = doc(db, COLLECTION, cacheKey);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    const data = snap.data();
    const activity = data?.activity;
    if (!activity || (!activity.title && !activity.steps)) return null;
    return {
      title: activity.title || '',
      objective: activity.objective || '',
      materials: Array.isArray(activity.materials) ? activity.materials : [],
      steps: Array.isArray(activity.steps) ? activity.steps : [],
    };
  } catch (err) {
    console.warn('[flowActivityCache] get failed:', err?.message);
    return null;
  }
}

/**
 * Store a generated flow activity for an artwork pair.
 * @param {Object} artwork1 - Primary artwork
 * @param {Object} artwork2 - Comparison artwork
 * @param {Object} activity - { title, objective?, materials, steps }
 */
export async function setCachedFlowActivity(artwork1, artwork2, activity) {
  const cacheKey = getFlowActivityCacheKey(artwork1, artwork2);
  if (!cacheKey || !activity) return;
  const id1 = artwork1?.id || artwork1?.imageUrl || '';
  const id2 = artwork2?.id || artwork2?.imageUrl || '';
  try {
    const ref = doc(db, COLLECTION, cacheKey);
    await setDoc(ref, {
      cacheKey,
      artwork1Id: id1,
      artwork2Id: id2,
      activity: {
        title: activity.title || '',
        objective: activity.objective || '',
        materials: Array.isArray(activity.materials) ? activity.materials : [],
        steps: Array.isArray(activity.steps) ? activity.steps : [],
      },
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }, { merge: true });
  } catch (err) {
    console.warn('[flowActivityCache] set failed:', err?.message);
  }
}
